import { DownloadOutlined, MailOutlined } from '@ant-design/icons';
import { ConfigProvider, message, Select, Space, Spin, Switch } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { debounce } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';

import {
  CONSENT_LEGEND_ORDER,
  CONSENT_STATUS_CONFIG,
} from '../../../../components/common/consentSignal/consentSignalStatus';
import {
  Button,
  DatePicker,
  SearchInput,
  TableWrapper,
} from '../../../../components/index';
import { useChangePagination } from '../../../../hooks';
import {
  useCreateStopoverDataExemptionMutation,
  useDeleteStopoverDataExemptionMutation,
  useLazyDownloadStopoverPendenciesQuery,
  useLazyEmailStopoverPendenciesQuery,
  useListStopoverPendenciesQuery,
} from '../../../../services/stopoverPendencyApi';
import {
  StopoverDataExemptionPayloadType,
  StopoverDataItemType,
  StopoverPendencyFiltersType,
  StopoverPendencyRowType,
} from '../../../../types';
import { downloadFile } from '../../../../utils/utils';
import { ReportsTable, SectionHeader } from '../styles';
import { StopoverDataCheckLegend } from './stopoverDataCheckCell';
import {
  exemptionKey,
  stopoverPendencyColumns,
} from './stopoverPendencyColumns';
import { StopoverPendencyDrawer } from './StopoverPendencyDrawer';

const PAGE_SIZE = 50;

const documentStatusOptions = [
  { label: 'Anuências: todas', value: '' },
  { label: 'Anuências: com pendência', value: 'PENDING_ANY' },
  ...CONSENT_LEGEND_ORDER.map((status) => ({
    label: CONSENT_STATUS_CONFIG[status].label,
    value: status,
  })),
];

const dataStatusOptions = [
  { label: 'Dados: todas', value: '' },
  { label: 'Dados: com pendência', value: 'PENDING' },
  { label: 'Dados: OK', value: 'OK' },
];

const ETA_FORMAT = 'DD/MM/YYYY';

function etaFilter(start: moment.Moment, end: moment.Moment) {
  return {
    // ISO com fuso: o backend espera data e hora e o fim do dia não pode
    // virar meia-noite.
    expected_arrival_after: start.clone().startOf('day').toISOString(true),
    expected_arrival_before: end.clone().endOf('day').toISOString(true),
  };
}

// Relatório "Pendências de Escalas" (PMIS-475): check go/no-go no formato da
// planilha "Check-List - Programação Suape", uma linha por atracação, com a
// conferência de dados (P/A/I) e a situação documental por anuente.
export function StopoverPendencySection() {
  const { onChangePagination, queryPage, setQueryPage } = useChangePagination();
  const [etaRange, setEtaRange] = useState<[moment.Moment, moment.Moment]>([
    moment().startOf('month'),
    moment().endOf('month'),
  ]);
  const [filters, setFilters] = useState<StopoverPendencyFiltersType>(() =>
    etaFilter(etaRange[0], etaRange[1])
  );
  const [selectedStopoverId, setSelectedStopoverId] = useState<number | null>(
    null
  );
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [createExemption] = useCreateStopoverDataExemptionMutation();
  const [deleteExemption] = useDeleteStopoverDataExemptionMutation();

  useEffect(() => setQueryPage(1), [filters, setQueryPage]);

  const { data, isFetching } = useListStopoverPendenciesQuery({
    ...filters,
    page: queryPage || 1,
    page_size: PAGE_SIZE,
  });
  const [download, { isFetching: isDownloading }] =
    useLazyDownloadStopoverPendenciesQuery();
  const [sendByEmail, { isFetching: isSending }] =
    useLazyEmailStopoverPendenciesQuery();

  async function changeExemption(
    row: StopoverPendencyRowType,
    item: StopoverDataItemType,
    exempt: boolean
  ) {
    const payload: StopoverDataExemptionPayloadType = {
      stopover: row.id,
      item,
      ...(row.docking_id ? { docking: row.docking_id } : {}),
    };
    setSavingKey(exemptionKey(row, item));
    try {
      if (exempt) {
        await createExemption(payload).unwrap();
      } else {
        await deleteExemption(payload).unwrap();
      }
    } catch {
      message.error('Erro ao salvar a isenção. Tente novamente.');
    } finally {
      setSavingKey(null);
    }
  }

  const columns = useMemo(
    () =>
      stopoverPendencyColumns(
        data?.check_columns || [],
        data?.consent_columns || [],
        {
          canEdit: true,
          savingKey,
          onExempt: (row, item) => changeExemption(row, item, true),
          onRemoveExemption: (row, item) => changeExemption(row, item, false),
        }
      ),
    [data?.check_columns, data?.consent_columns, savingKey]
  );

  function updateFilter(changes: Partial<StopoverPendencyFiltersType>) {
    setFilters((previous) => ({ ...previous, ...changes }));
  }

  // Mesma assinatura do DockingsSection: o DatePicker tipa o onChange como união
  // de picker simples e de intervalo.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function onChangeEtaRange(range: any) {
    if (range?.[0] && range?.[1]) {
      setEtaRange([range[0], range[1]]);
      updateFilter(etaFilter(range[0], range[1]));
    } else {
      // Sem período a tela lista tudo; a exportação usa o mês atual.
      updateFilter({
        expected_arrival_after: undefined,
        expected_arrival_before: undefined,
      });
    }
  }

  async function handleDownload(fileFormat: 'xlsx' | 'csv') {
    try {
      const file = await download({
        ...filters,
        file_format: fileFormat,
      }).unwrap();
      // downloadFile embrulha o conteúdo num Blob; um Blob passa intacto.
      downloadFile(
        file as unknown as string,
        `pendencias_escalas.${fileFormat}`
      );
    } catch {
      message.error('Erro ao gerar o arquivo. Tente novamente.');
    }
  }

  async function handleSendByEmail() {
    try {
      await sendByEmail(filters).unwrap();
      message.success(
        'Exportação em processamento. Você a receberá em seu e-mail em breve.',
        5
      );
    } catch {
      message.error('Erro ao solicitar a exportação. Tente novamente.');
    }
  }

  return (
    <>
      <SectionHeader style={{ flexDirection: 'column', gap: '15px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <SearchInput
            size="large"
            allowClear
            placeholder="Pesquisar navio / IMO / DUV / N° escala / agência"
            style={{ width: '400px' }}
            onChange={debounce(
              (evt) => updateFilter({ search: evt.target.value || undefined }),
              500
            )}
          />
          <Space size={12}>
            <Button
              icon={<DownloadOutlined />}
              size="large"
              loading={isDownloading}
              onClick={() => handleDownload('xlsx')}
            >
              Baixar XLS
            </Button>
            <Button
              icon={<DownloadOutlined />}
              size="large"
              loading={isDownloading}
              onClick={() => handleDownload('csv')}
            >
              Baixar CSV
            </Button>
            <Button
              icon={<MailOutlined />}
              size="large"
              loading={isSending}
              onClick={handleSendByEmail}
            >
              Enviar por e-mail
            </Button>
          </Space>
        </div>
        <div
          style={{
            display: 'flex',
            gap: '16px',
            marginRight: 'auto',
            marginBottom: '20px',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Select
            defaultValue=""
            options={documentStatusOptions}
            style={{ width: '320px' }}
            onChange={(value: string) =>
              updateFilter({
                document_status: (value ||
                  undefined) as StopoverPendencyFiltersType['document_status'],
              })
            }
          />
          <Select
            defaultValue=""
            options={dataStatusOptions}
            style={{ width: '220px' }}
            onChange={(value: string) =>
              updateFilter({
                data_status: (value ||
                  undefined) as StopoverPendencyFiltersType['data_status'],
              })
            }
          />
          <span>Período de ETA:</span>
          <DatePicker
            allowClear
            isRangePicker
            showTime={false}
            format={[ETA_FORMAT]}
            style={{ width: '240px' }}
            minuteStep={1}
            // O DatePicker tipa o valor como união de simples e intervalo.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            defaultValue={etaRange as any}
            onChange={onChangeEtaRange}
          />
          <Space size={8}>
            <Switch
              checked={!!filters.show_all}
              onChange={(checked) =>
                updateFilter({ show_all: checked || undefined })
              }
            />
            <span>Mostrar todas (inclui encerradas e canceladas)</span>
          </Space>
          <StopoverDataCheckLegend />
        </div>
      </SectionHeader>
      <Spin spinning={isFetching} tip="Carregando...">
        <TableWrapper>
          <ConfigProvider
            renderEmpty={() => (
              <span>Nenhuma escala para os filtros escolhidos</span>
            )}
          >
            <ReportsTable
              rowKey={(row) =>
                `${(row as StopoverPendencyRowType).id}-${
                  (row as StopoverPendencyRowType).docking_id
                }`
              }
              onRow={(row) => ({
                onClick: () =>
                  setSelectedStopoverId((row as StopoverPendencyRowType).id),
                style: { cursor: 'pointer' },
              })}
              // ReportsTable é tipada com linhas genéricas.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              columns={columns as ColumnsType<any>}
              dataSource={data?.results || []}
              pagination={{
                position: ['bottomCenter'],
                pageSize: PAGE_SIZE,
                total: data?.count,
                current: queryPage || 1,
                onChange: onChangePagination,
                showSizeChanger: false,
              }}
              scroll={{ x: 'max-content', y: '60vh' }}
            />
          </ConfigProvider>
        </TableWrapper>
      </Spin>
      <StopoverPendencyDrawer
        stopoverId={selectedStopoverId}
        onClose={() => setSelectedStopoverId(null)}
      />
    </>
  );
}
